import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NativeCheckbox } from '@/components/ui/NativeCheckbox';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import DraggablePlayerList from '@/components/DraggablePlayerList';
import UserProfileModal from '@/components/UserProfileModal';


// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      times.push(timeString);
    }
  }
  return times;
};

const TIME_OPTIONS = generateTimeOptions();

export default function AdminGames({ user, onLogout }) {
  const [games, setGames] = useState([]);
  const [archivedGames, setArchivedGames] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' or 'archived'
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [showReorderDialog, setShowReorderDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState(''); // Search filter for users
  const [addPlayerMode, setAddPlayerMode] = useState('existing'); // 'existing' or 'guest'
  const [addPlayerData, setAddPlayerData] = useState({
    user_id: '',
    name: '',
    surname: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    hall: '',
    date: '',
    start_time: '',
    end_time: '',
    player_limit: 12,
    fee: 5,
    payment_link: '',
    prioritize_by_rating: false,
    enable_player_rating: true,  // Default: enabled
    notify_users: false
  });
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    loadGames();
    loadUsers();
  }, []);

  const loadGames = async () => {
    try {
      const response = await api.get('/games');
      // Sort by date (nearest first)
      const sortByDate = (a, b) => new Date(a.date) - new Date(b.date);
      
      const active = response.data.filter(g => !g.is_archived).sort(sortByDate);
      const archived = response.data.filter(g => g.is_archived).sort(sortByDate);
      setGames(active);
      setArchivedGames(archived);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      // Filter only approved users
      setAllUsers(response.data.filter(u => u.is_approved));
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    
    // Validate required fields that don't have native HTML validation
    if (!formData.start_time) {
      toast.error('Please select a start time');
      return;
    }
    if (!formData.end_time) {
      toast.error('Please select an end time');
      return;
    }
    
    // Validate end time is after start time
    if (formData.start_time >= formData.end_time) {
      toast.error('End time must be after start time');
      return;
    }
    
    try {
      await api.post('/games', formData);
      toast.success('Game created successfully!');
      setShowCreateDialog(false);
      setFormData({
        title: '',
        location: '',
        hall: '',
        date: '',
        start_time: '',
        end_time: '',
        player_limit: 12,
        fee: 5,
        payment_link: '',
        prioritize_by_rating: false,
        enable_player_rating: true,
        notify_users: false
      });
      loadGames();
    } catch (error) {
      toast.error('Failed to create game');
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (window.confirm('Are you sure you want to cancel this game? All registered players will be notified.')) {
      try {
        await api.delete(`/games/${gameId}`);
        toast.success('Game cancelled and players notified');
        loadGames();
      } catch (error) {
        toast.error('Failed to cancel game');
      }
    }
  };

  const handleEditGame = (game) => {
    setSelectedGame(game);
    setFormData({
      title: game.title,
      location: game.location,
      hall: game.hall || '',
      date: game.date,
      start_time: game.start_time,
      end_time: game.end_time,
      player_limit: game.player_limit,
      fee: game.fee,
      payment_link: game.payment_link || '',
      prioritize_by_rating: game.prioritize_by_rating || false,
      enable_player_rating: game.enable_player_rating !== undefined ? game.enable_player_rating : true,
      notify_users: false
    });
    setShowEditDialog(true);
  };

  const handleCopyGame = (game) => {
    // Copy game details but exclude date, payment_link, and player lists
    setFormData({
      title: game.title,
      location: game.location,
      hall: game.hall || '',
      date: '', // Empty date for new game
      start_time: game.start_time,
      end_time: game.end_time,
      player_limit: game.player_limit,
      fee: game.fee,
      payment_link: '', // Empty payment link
      prioritize_by_rating: game.prioritize_by_rating || false,
      enable_player_rating: game.enable_player_rating !== undefined ? game.enable_player_rating : true,
      notify_users: false
    });
    setShowCreateDialog(true);
    toast.success('Game details copied! Set the date to create a new game.');
  };


  const handleUpdateGame = async (e) => {
    e.preventDefault();
    
    // Validate required time fields
    if (!formData.start_time) {
      toast.error('Please select a start time');
      return;
    }
    if (!formData.end_time) {
      toast.error('Please select an end time');
      return;
    }
    
    // Validate end time is after start time
    if (formData.start_time >= formData.end_time) {
      toast.error('End time must be after start time');
      return;
    }
    
    try {
      // Only send fields that have actually changed
      const changedFields = { notify_users: formData.notify_users };
      
      if (formData.title !== selectedGame.title) changedFields.title = formData.title;
      if (formData.location !== selectedGame.location) changedFields.location = formData.location;
      if (formData.hall !== (selectedGame.hall || '')) changedFields.hall = formData.hall;
      if (formData.date !== selectedGame.date) changedFields.date = formData.date;
      if (formData.start_time !== selectedGame.start_time) changedFields.start_time = formData.start_time;
      if (formData.end_time !== selectedGame.end_time) changedFields.end_time = formData.end_time;
      if (formData.player_limit !== selectedGame.player_limit) changedFields.player_limit = formData.player_limit;
      if (formData.fee !== selectedGame.fee) changedFields.fee = formData.fee;
      if (formData.payment_link !== (selectedGame.payment_link || '')) changedFields.payment_link = formData.payment_link;
      if (formData.prioritize_by_rating !== (selectedGame.prioritize_by_rating || false)) changedFields.prioritize_by_rating = formData.prioritize_by_rating;
      if (formData.enable_player_rating !== (selectedGame.enable_player_rating !== undefined ? selectedGame.enable_player_rating : true)) changedFields.enable_player_rating = formData.enable_player_rating;
      
      await api.put(`/games/${selectedGame.id}`, changedFields);
      toast.success(formData.notify_users ? 'Game updated and players notified!' : 'Game updated successfully!');
      setShowEditDialog(false);
      setSelectedGame(null);
      loadGames();
    } catch (error) {
      toast.error('Failed to update game');
    }
  };



  const handleReorderPlayers = async (registeredPlayers, waitingList) => {
    try {
      await api.post(`/games/${selectedGame.id}/reorder-players`, {
        registered_players: registeredPlayers.map(p => p.user_id),
        waiting_list: waitingList.map(p => p.user_id)
      });
      
      toast.success('Player order updated successfully');
      setShowReorderDialog(false);
      loadGames();
    } catch (error) {
      console.error('Failed to reorder players:', error);
      toast.error(error.response?.data?.detail || 'Failed to update player order');
      throw error;
    }
  };

  const handleReorderClick = (game) => {
    setSelectedGame(game);
    setShowReorderDialog(true);
  };


  const handleAddPlayerClick = (game) => {
    setSelectedGame(game);
    setAddPlayerMode('existing');
    setAddPlayerData({ user_id: '', name: '', surname: '' });
    setUserSearchQuery(''); // Reset search when opening dialog
    setShowAddPlayerDialog(true);
  };

  // Filter users based on search query
  const filteredUsers = allUsers.filter(u => {
    if (!userSearchQuery) return true;
    const searchLower = userSearchQuery.toLowerCase();
    return (
      u.first_name?.toLowerCase().includes(searchLower) ||
      u.last_name?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    try {
      const payload = addPlayerMode === 'existing' 
        ? { user_id: addPlayerData.user_id }
        : { name: addPlayerData.name, surname: addPlayerData.surname };
      
      await api.post(`/games/${selectedGame.id}/admin-add-player`, payload);
      toast.success('Player added successfully!');
      setShowAddPlayerDialog(false);
      setAddPlayerData({ user_id: '', name: '', surname: '' });
      loadGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    }
  };

  const handleRemovePlayer = async (gameId, userId) => {
    if (window.confirm('Remove this player from the game?')) {
      try {
        await api.delete(`/games/${gameId}/admin-remove-user/${userId}`);
        toast.success('Player removed successfully');
        loadGames();
      } catch (error) {
        toast.error('Failed to remove player');
      }
    }
  };

  const handleMarkPayment = async (gameId, userId, paid) => {
    try {
      await api.post(`/games/${gameId}/admin-payment`, {
        user_id: userId,
        paid: paid
      });
      await loadGames();
      toast.success(paid ? 'Marked as paid' : 'Marked as unpaid');
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleMoveToArchived = async (gameId) => {
    if (window.confirm('Move this game to archived games section?')) {
      try {
        await api.post(`/games/${gameId}/move-to-archived`);
        toast.success('Game moved to archived games');
        loadGames();
      } catch (error) {
        toast.error('Failed to move game');
      }
    }
  };

  const handleUnarchive = async (gameId) => {
    if (window.confirm('Move this game back to upcoming games?')) {
      try {
        await api.post(`/games/${gameId}/unarchive`);
        toast.success('Game moved to upcoming games');
        loadGames();
      } catch (error) {
        toast.error('Failed to unarchive game');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-2xl font-semibold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Manage Games</h1>
            <p className="text-xl text-gray-600">Create and manage volleyball games</p>
          </div>

          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-game-btn"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-6 py-6 rounded-full"
              >
                + Create Game
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-0">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create New Game</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateGame} className="space-y-4 mt-4 pb-4">
                <div>
                  <Label htmlFor="title">Game Title *</Label>
                  <Input
                    id="title"
                    data-testid="game-title-input"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="mt-2"
                    placeholder="e.g., Friday Night Volleyball"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Venue (Sports Hall Name) *</Label>
                    <Input
                      id="location"
                      data-testid="game-location-input"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., USC Amsterdam"
                    />
                  </div>
                  <div>
                    <Label htmlFor="hall">Hall Name (Optional)</Label>
                    <Input
                      id="hall"
                      value={formData.hall}
                      onChange={(e) => setFormData({...formData, hall: e.target.value})}
                      className="mt-2"
                      placeholder="e.g., Hall 2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    data-testid="game-date-input"
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Select
                      value={formData.start_time}
                      onValueChange={(value) => setFormData({...formData, start_time: value})}
                    >
                      <SelectTrigger className="mt-2" id="start_time">
                        <SelectValue placeholder="Select start time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {TIME_OPTIONS.map((time) => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="end_time">End Time *</Label>
                    <Select
                      value={formData.end_time}
                      onValueChange={(value) => setFormData({...formData, end_time: value})}
                    >
                      <SelectTrigger className="mt-2" id="end_time">
                        <SelectValue placeholder="Select end time" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {TIME_OPTIONS.map((time) => (
                          <SelectItem key={time} value={time}>{time}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="player_limit">Player Limit *</Label>
                    <Input
                      id="player_limit"
                      data-testid="game-player-limit-input"
                      type="number"
                      min="2"
                      required
                      value={formData.player_limit}
                      onChange={(e) => setFormData({...formData, player_limit: parseInt(e.target.value)})}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="fee">Fee (€) *</Label>
                    <Input
                      id="fee"
                      data-testid="game-fee-input"
                      type="number"
                      min="0"
                      step="0.5"
                      required
                      value={formData.fee}
                      onChange={(e) => setFormData({...formData, fee: parseFloat(e.target.value)})}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="payment_link">Payment Link (Optional)</Label>
                  <Input
                    id="payment_link"
                    data-testid="game-payment-link-input"
                    type="url"
                    placeholder="https://..."
                    value={formData.payment_link}
                    onChange={(e) => setFormData({...formData, payment_link: e.target.value})}
                    className="mt-2"
                  />
                </div>

                <div className="flex items-center space-x-2 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                  <input
                    id="prioritize_by_rating"
                    type="checkbox"
                    checked={formData.prioritize_by_rating}
                    onChange={(e) => setFormData({...formData, prioritize_by_rating: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <div>
                    <Label htmlFor="prioritize_by_rating" className="cursor-pointer font-bold text-purple-900">
                      Prioritize players by rating
                    </Label>
                    <p className="text-xs text-purple-700 mt-1">When enabled, players with higher ratings will be prioritized for registration</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                  <input
                    id="enable_player_rating"
                    type="checkbox"
                    checked={formData.enable_player_rating}
                    onChange={(e) => setFormData({...formData, enable_player_rating: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <div>
                    <Label htmlFor="enable_player_rating" className="cursor-pointer font-bold text-green-900">
                      Enable Player Rating
                    </Label>
                    <p className="text-xs text-green-700 mt-1">
                      When enabled, players can rate each other after this game. 
                      Turn off for training sessions or casual games.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    id="notify_users"
                    data-testid="notify-users-checkbox"
                    type="checkbox"
                    checked={formData.notify_users}
                    onChange={(e) => setFormData({...formData, notify_users: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="notify_users" className="cursor-pointer">
                    Send email notification to all users
                  </Label>
                </div>

                <Button
                  data-testid="submit-create-game-btn"
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-6"
                >
                  Create Game
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8 flex gap-4 border-b-2 border-gray-200">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'upcoming'
                ? 'text-orange-600 border-b-4 border-orange-600 -mb-0.5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Upcoming Games ({games.length})
          </button>
          <button
            onClick={() => setActiveTab('archived')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'archived'
                ? 'text-orange-600 border-b-4 border-orange-600 -mb-0.5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Archived Games ({archivedGames.length})
          </button>
        </div>

        {/* Upcoming Games Tab */}
        {activeTab === 'upcoming' && (
          <>
            {games.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">🏐</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Games Yet</h2>
            <p className="text-gray-600">Create your first game to get started!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {games.map((game) => (
              <div
                key={game.id}
                data-testid={`admin-game-card-${game.id}`}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{game.title}</h3>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline flex items-center gap-1 mb-2"
                    >
                      📍 {game.location}{game.hall ? ` - ${game.hall}` : ''}
                    </a>
                    <p className="text-gray-900 font-bold mb-2 flex items-center gap-1">
                      📅 {game.date} • {game.start_time} - {game.end_time}
                    </p>
                    {game.payment_link && (
                      <a
                        href={game.payment_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-bold text-sm flex items-center gap-2 mb-3"
                      >
                        💳 Payment Link
                      </a>
                    )}

                    <div className="flex flex-wrap gap-3 mb-3">
                      <Badge className="bg-blue-100 text-blue-700">
                        {game.registered_players.length}/{game.player_limit} Players
                      </Badge>
                      {game.waiting_list.length > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          {game.waiting_list.length} Waiting
                        </Badge>
                      )}
                      <Badge className="bg-green-100 text-green-700">
                        €{game.fee}
                      </Badge>
                    </div>

                    {/* Registered Players Table */}
                    {game.registered_players_info && game.registered_players_info.length > 0 && (
                      <div className="mt-4 bg-green-50 rounded-lg p-4">
                        <p className="font-semibold text-green-900 mb-3">✅ Registered Players:</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-green-200">
                                <th className="text-left py-2 px-2 text-green-800">#</th>
                                <th className="text-left py-2 px-2 text-green-800">Name</th>
                                <th className="text-center py-2 px-2 text-green-800">Payment</th>
                                <th className="text-center py-2 px-2 text-green-800">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {game.registered_players_info.map((player, index) => (
                                <tr key={player.user_id} className="border-b border-green-100">
                                  <td className="py-2 px-2 font-semibold text-green-700">{index + 1}</td>
                                  <td className={`py-2 px-2 ${!player.paid ? 'text-red-600 font-semibold' : 'text-green-900'}`}>
                                    <button type="button" data-testid={`player-link-${player.user_id}`} onClick={() => setProfileUserId(player.user_id)} className="hover:text-orange-600 text-left font-medium cursor-pointer">
                                      {player.name}
                                    </button>{player.is_first_game && ' 🍒'}
                                    {player.is_admin && (
                                      <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    {player.paid ? (
                                      <span className="text-green-600 font-semibold">✓ Paid</span>
                                    ) : (
                                      <span className="text-red-600 font-semibold">✗ Unpaid</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        data-testid={`admin-toggle-payment-${game.id}-${player.user_id}`}
                                        className={`h-6 px-2 text-xs ${player.paid ? 'text-orange-600' : 'text-green-600'}`}
                                        onClick={() => handleMarkPayment(game.id, player.user_id, !player.paid)}
                                      >
                                        {player.paid ? 'Unpaid' : 'Paid'}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2 text-xs text-red-600"
                                        onClick={() => handleRemovePlayer(game.id, player.user_id)}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    
                    {/* Waiting List Table */}
                    {game.waiting_list_info && game.waiting_list_info.length > 0 && (
                      <div className="mt-4 bg-yellow-50 rounded-lg p-4">
                        <p className="font-semibold text-yellow-900 mb-3">⏳ Waiting List:</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-yellow-200">
                                <th className="text-left py-2 px-2 text-yellow-800">#</th>
                                <th className="text-left py-2 px-2 text-yellow-800">Name</th>
                                <th className="text-center py-2 px-2 text-yellow-800">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {game.waiting_list_info.map((player, index) => (
                                <tr key={player.user_id} className="border-b border-yellow-100">
                                  <td className="py-2 px-2 font-semibold text-yellow-700">{index + 1}</td>
                                  <td className="py-2 px-2 text-yellow-900">
                                    <button type="button" data-testid={`player-link-${player.user_id}`} onClick={() => setProfileUserId(player.user_id)} className="hover:text-orange-600 text-left font-medium cursor-pointer">
                                      {player.name}
                                    </button>{player.is_first_game && ' 🍒'}
                                    {player.is_admin && (
                                      <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-2 text-center">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs text-red-600"
                                      onClick={() => handleRemovePlayer(game.id, player.user_id)}
                                    >
                                      Remove
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Admin Action Buttons */}
                  <div className="flex flex-col gap-2 w-full md:w-auto">
                    <Button
                      onClick={() => handleCopyGame(game)}
                      variant="outline"
                      className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full"
                    >
                      📋 Copy Game
                    </Button>
                    <Button
                      onClick={() => handleEditGame(game)}
                      variant="outline"
                      className="border-blue-500 text-blue-600 hover:bg-blue-50 w-full"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleAddPlayerClick(game)}
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50 w-full"
                    >
                      Add Player
                    </Button>
                    <Button
                      onClick={() => handleReorderClick(game)}
                      variant="outline"
                      className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full"
                    >
                      🔄 Reorder Players
                    </Button>
                    <Button
                      onClick={() => handleMoveToArchived(game.id)}
                      variant="outline"
                      className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 w-full"
                    >
                      Move to Archived
                    </Button>
                    <Button
                      data-testid={`delete-game-btn-${game.id}`}
                      onClick={() => handleDeleteGame(game.id)}
                      variant="outline"
                      className="border-red-500 text-red-600 hover:bg-red-50 w-full"
                    >
                      Cancel Game
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}

        {/* Archived Games Tab */}
        {activeTab === 'archived' && (
          <>
            {archivedGames.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">📦</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">No Archived Games</h2>
                <p className="text-gray-600">Archived games will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {archivedGames.map((game) => (
                  <div
                    key={game.id}
                    className="bg-white rounded-2xl shadow-lg p-6"
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">{game.title || game.location}</h3>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(game.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1 mb-2"
                        >
                          📍 {game.location}{game.hall ? ` - ${game.hall}` : ''}
                        </a>
                        <p className="text-gray-900 font-bold mb-2 flex items-center gap-1">
                          📅 {game.date} • {game.start_time} - {game.end_time}
                        </p>
                        {game.payment_link && (
                          <a
                            href={game.payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline font-bold text-sm flex items-center gap-2 mb-3"
                          >
                            💳 Payment Link
                          </a>
                        )}

                        <div className="flex flex-wrap gap-3 mb-3">
                          <Badge className="bg-gray-400 text-white">Archived</Badge>
                          <Badge className="bg-blue-100 text-blue-700">
                            {game.registered_players.length}/{game.player_limit} Players
                          </Badge>
                          {game.waiting_list.length > 0 && (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              {game.waiting_list.length} Waiting
                            </Badge>
                          )}
                          <Badge className="bg-green-100 text-green-700">
                            €{game.fee}
                          </Badge>
                        </div>

                        {/* Registered Players Table */}
                        {game.registered_players_info && game.registered_players_info.length > 0 && (
                          <div className="mt-4 bg-green-50 rounded-lg p-4">
                            <p className="font-semibold text-green-900 mb-3">✅ Registered Players:</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-green-200">
                                    <th className="text-left py-2 px-2 text-green-800">#</th>
                                    <th className="text-left py-2 px-2 text-green-800">Name</th>
                                    <th className="text-center py-2 px-2 text-green-800">Payment</th>
                                    <th className="text-center py-2 px-2 text-green-800">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {game.registered_players_info.map((player, index) => (
                                    <tr key={player.user_id} className="border-b border-green-100">
                                      <td className="py-2 px-2 font-semibold text-green-700">{index + 1}</td>
                                      <td className={`py-2 px-2 ${!player.paid ? 'text-red-600 font-semibold' : 'text-green-900'}`}>
                                        <button type="button" data-testid={`player-link-${player.user_id}`} onClick={() => setProfileUserId(player.user_id)} className="hover:text-orange-600 text-left font-medium cursor-pointer">
                                          {player.name}
                                        </button>{player.is_first_game && ' 🍒'}
                                        {player.is_admin && (
                                          <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        {player.paid ? (
                                          <span className="text-green-600 font-semibold">✓ Paid</span>
                                        ) : (
                                          <span className="text-red-600 font-semibold">✗ Unpaid</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        <div className="flex gap-1 justify-center">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            data-testid={`admin-toggle-payment-${game.id}-${player.user_id}`}
                                            className={`h-6 px-2 text-xs ${player.paid ? 'text-orange-600' : 'text-green-600'}`}
                                            onClick={() => handleMarkPayment(game.id, player.user_id, !player.paid)}
                                          >
                                            {player.paid ? 'Unpaid' : 'Paid'}
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 text-xs text-red-600"
                                            onClick={() => handleRemovePlayer(game.id, player.user_id)}
                                          >
                                            Remove
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Waiting List Table */}
                        {game.waiting_list_info && game.waiting_list_info.length > 0 && (
                          <div className="mt-4 bg-yellow-50 rounded-lg p-4">
                            <p className="font-semibold text-yellow-900 mb-3">⏳ Waiting List:</p>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-yellow-200">
                                    <th className="text-left py-2 px-2 text-yellow-800">#</th>
                                    <th className="text-left py-2 px-2 text-yellow-800">Name</th>
                                    <th className="text-center py-2 px-2 text-yellow-800">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {game.waiting_list_info.map((player, index) => (
                                    <tr key={player.user_id} className="border-b border-yellow-100">
                                      <td className="py-2 px-2 font-semibold text-yellow-700">{index + 1}</td>
                                      <td className="py-2 px-2 text-yellow-900">
                                    <button type="button" data-testid={`player-link-${player.user_id}`} onClick={() => setProfileUserId(player.user_id)} className="hover:text-orange-600 text-left font-medium cursor-pointer">
                                      {player.name}
                                    </button>{player.is_first_game && ' 🍒'}
                                    {player.is_admin && (
                                      <span className="ml-1 inline-block align-middle bg-orange-100 text-orange-700 text-[9px] font-semibold px-1 py-px rounded leading-none">A</span>
                                    )}
                                  </td>
                                      <td className="py-2 px-2 text-center">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 px-2 text-xs text-red-600"
                                          onClick={() => handleRemovePlayer(game.id, player.user_id)}
                                        >
                                          Remove
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Admin actions for archived games */}
                    <div className="mt-6 flex flex-col gap-2 w-full md:w-auto">
                      <Button
                        onClick={() => handleCopyGame(game)}
                        variant="outline"
                        className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full"
                      >
                        📋 Copy Game
                      </Button>
                      <Button
                        onClick={() => handleEditGame(game)}
                        variant="outline"
                        className="border-blue-500 text-blue-600 hover:bg-blue-50 w-full"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleAddPlayerClick(game)}
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50 w-full"
                      >
                        Add Player
                      </Button>
                      <Button
                        onClick={() => handleReorderClick(game)}
                        variant="outline"
                        className="border-purple-500 text-purple-600 hover:bg-purple-50 w-full"
                      >
                        🔄 Reorder Players
                      </Button>
                      <Button
                        onClick={() => handleUnarchive(game.id)}
                        variant="outline"
                        className="border-indigo-500 text-indigo-600 hover:bg-indigo-50 w-full"
                      >
                        Unarchive
                      </Button>
                      <Button
                        onClick={() => handleDeleteGame(game.id)}
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-50 w-full"
                      >
                        Delete Game
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Edit Game Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full mx-2 sm:mx-0">
            <DialogHeader>
              <DialogTitle>Edit Game</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateGame} className="space-y-6 pb-4">
              <div>
                <Label htmlFor="edit-title">Game Title *</Label>
                <Input
                  id="edit-title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-location">Venue (Sports Hall Name) *</Label>
                  <Input
                    id="edit-location"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="mt-2"
                    placeholder="e.g., USC Amsterdam"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-hall">Hall Name (Optional)</Label>
                  <Input
                    id="edit-hall"
                    value={formData.hall}
                    onChange={(e) => setFormData({...formData, hall: e.target.value})}
                    className="mt-2"
                    placeholder="e.g., Hall 2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-date">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-start-time">Start Time *</Label>
                  <Select
                    value={formData.start_time}
                    onValueChange={(value) => setFormData({...formData, start_time: value})}
                  >
                    <SelectTrigger className="mt-2" id="edit-start-time">
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {TIME_OPTIONS.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-end-time">End Time *</Label>
                  <Select
                    value={formData.end_time}
                    onValueChange={(value) => setFormData({...formData, end_time: value})}
                  >
                    <SelectTrigger className="mt-2" id="edit-end-time">
                      <SelectValue placeholder="Select end time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {TIME_OPTIONS.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-player-limit">Player Limit *</Label>
                  <Input
                    id="edit-player-limit"
                    type="number"
                    min="1"
                    required
                    value={formData.player_limit}
                    onChange={(e) => setFormData({...formData, player_limit: parseInt(e.target.value)})}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-fee">Fee (€) *</Label>
                  <Input
                    id="edit-fee"
                    type="number"
                    min="0"
                    step="0.5"
                    required
                    value={formData.fee}
                    onChange={(e) => setFormData({...formData, fee: parseFloat(e.target.value)})}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-payment-link">Payment Link (Optional)</Label>
                <Input
                  id="edit-payment-link"
                  type="url"
                  placeholder="https://..."
                  value={formData.payment_link}
                  onChange={(e) => setFormData({...formData, payment_link: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="flex items-center space-x-2 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <input
                  id="edit-prioritize-rating"
                  type="checkbox"
                  checked={formData.prioritize_by_rating}
                  onChange={(e) => setFormData({...formData, prioritize_by_rating: e.target.checked})}
                  className="w-4 h-4"
                />
                <div>
                  <Label htmlFor="edit-prioritize-rating" className="cursor-pointer font-bold text-purple-900">
                    Prioritize players by rating
                  </Label>
                  <p className="text-xs text-purple-700 mt-1">When enabled, players with higher ratings will be prioritized for registration</p>
                </div>
              </div>

              <div className="flex items-center space-x-2 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                <input
                  id="edit-enable-rating"
                  type="checkbox"
                  checked={formData.enable_player_rating}
                  onChange={(e) => setFormData({...formData, enable_player_rating: e.target.checked})}
                  className="w-4 h-4"
                />
                <div>
                  <Label htmlFor="edit-enable-rating" className="cursor-pointer font-bold text-green-900">
                    Enable Player Rating
                  </Label>
                  <p className="text-xs text-green-700 mt-1">
                    When enabled, players can rate each other after this game. 
                    Turn off for training sessions or casual games.
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <NativeCheckbox
                  id="edit-notify"
                  checked={formData.notify_users}
                  onChange={(checked) => setFormData({...formData, notify_users: checked})}
                />
                <Label htmlFor="edit-notify" className="cursor-pointer">
                  Send email notification to all registered players about changes
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-6"
              >
                Update Game
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Player Dialog */}
        <Dialog open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Player to Game</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddPlayer} className="space-y-6">
              <div>
                <Label>Player Type</Label>
                <div className="flex gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <NativeCheckbox
                      id="mode-existing"
                      checked={addPlayerMode === 'existing'}
                      onChange={(checked) => setAddPlayerMode('existing')}
                    />
                    <label htmlFor="mode-existing" className="text-sm font-medium cursor-pointer">
                      Existing Member
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <NativeCheckbox
                      id="mode-guest"
                      checked={addPlayerMode === 'guest'}
                      onChange={(checked) => setAddPlayerMode('guest')}
                    />
                    <label htmlFor="mode-guest" className="text-sm font-medium cursor-pointer">
                      Guest (Non-Member)
                    </label>
                  </div>
                </div>
              </div>

              {addPlayerMode === 'existing' ? (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="user-search">Search User</Label>
                    <Input
                      id="user-search"
                      type="text"
                      placeholder="Search by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="select-user">Select User *</Label>
                    <Select
                      value={addPlayerData.user_id}
                      onValueChange={(value) => setAddPlayerData({...addPlayerData, user_id: value})}
                      required
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Choose a user..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.first_name} {u.last_name} ({u.email})
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-2 py-4 text-sm text-gray-500 text-center">
                            No users found matching "{userSearchQuery}"
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Showing {filteredUsers.length} of {allUsers.length} users
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="guest-name">First Name *</Label>
                    <Input
                      id="guest-name"
                      required
                      value={addPlayerData.name}
                      onChange={(e) => setAddPlayerData({...addPlayerData, name: e.target.value})}
                      className="mt-2"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="guest-surname">Last Name *</Label>
                    <Input
                      id="guest-surname"
                      required
                      value={addPlayerData.surname}
                      onChange={(e) => setAddPlayerData({...addPlayerData, surname: e.target.value})}
                      className="mt-2"
                      placeholder="Doe"
                    />
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-6"
              >
                Add Player
              </Button>
            </form>
          </DialogContent>
        </Dialog>


        {/* Reorder Players Dialog */}
        <Dialog open={showReorderDialog} onOpenChange={setShowReorderDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Reorder Players - {selectedGame?.title}</DialogTitle>
            </DialogHeader>
            {selectedGame && (
              <DraggablePlayerList
                registeredPlayers={selectedGame.registered_players_info || []}
                waitingList={selectedGame.waiting_list_info || []}
                onReorder={handleReorderPlayers}
                playerLimit={selectedGame.player_limit}
              />
            )}
          </DialogContent>
        </Dialog>

        <UserProfileModal
          userId={profileUserId}
          open={!!profileUserId}
          onClose={() => setProfileUserId(null)}
        />

      </div>
    </div>
  );
}
